import { ReactComponent as IconExxoMobil } from "@/assets/svgs/exxonmobil.svg";
import { assetUrl } from '@/utils/asset-url';
import { ReactComponent as IconClose } from "@/assets/svgs/icon-close.svg";
import { ReactComponent as IconSearch } from "@/assets/svgs/icon-search.svg";
import Breadcrumbs from "@/components/Breadcrumbs";
import Button from "@/components/Button/index";
import Input from "@/components/Input/index";
import Portal from "@/components/Portal";
import StepList from "@/components/StepList";
import useModal from "@/hooks/use-modal";
import usePage from "@/hooks/use-page/index";
import usePrincipals from "@/hooks/use-principal/index";
import useStepList from "@/hooks/use-step-list";
import { useLocation, useNavigate, useParams } from "@/lib/router-dom/index";
import API_ENROLMENT from "@/services/api/enrolment";
import API_SUBSCRIPTIONS from "@/services/api/subscriptions";
import rem from "@/utils/rem.js";
import produce from "immer";
import { useEffect, useState } from "react";
import ConfirmRemovalOfPrincipalModal from "../components/ConfirmRemovalOfPrincipalModal/index";
import PrincipalTableList from "./principal-table";
import { EnrolmentTypes } from "@/constants/use-case/enrolment";
import Stepper from "@/componentsDS/Stepper/Stepper";
import { feature } from "@/utils/feature-verifier";
import { featureConstant } from "@/constants/configuration/feature";

const NoMessage = ({ text }) => {
	return (
		<div className="data-table__empty__icon">
			<span>{text}</span>
		</div>
	);
};

const AddingPrincipalTable = ({ principals, prevStep }) => {
	return (
		<div className="adding-principal-data-table__content">
			<div className="adding-principal-table__table">
				<div className="principal-table__table__head__row">
					<div
						data-testid={`adding-principal-table__header`}
						className={`principal-table__header`}
						id="principals"
					>
						Adding Principal
					</div>
					<div className="adding-principal-edit--button">
						<Button
							data-testid="edit-button"
							text="Edit"
							ghost
							submit={() => prevStep()}
						/>
					</div>
				</div>
				{principals?.length > 0 ? (
					<>
						{principals
							.map((item) => {
								return (
									<div
										className="adding-principal_table__body__rows"
										key={item.id}
									>
										<div className="adding-principal_table_data__row">
											<div
												className="layout-request__tile__content__avatar"
												style={{
													backgroundImage: `url(${item?.organisation?.iconUrl})`,
												}}
											>
												{!item?.organisation?.iconUrl && <IconExxoMobil />}
											</div>
											<div className="adding-principal_data__col__org_name_uen">
												<div>{item.name}</div>
												<div>
													UEN:
													{item.uen}
												</div>
											</div>
										</div>
									</div>
								);
							})}
					</>
				) : (
					<div className="data-table__empty">
						<NoMessage text="No principal to add" />
					</div>
				)}
			</div>
		</div>
	);
};

const RemovingPrincipalTable = ({ principals, prevStep }) => {
	return (
		<div className="removing-principal-data-table__content">
			<div className="removing-principal-table__table">
				<div className="principal-table__table__head__row">
					<div
						className={`principal-table__header`}
						data-testid={`removing-principal-table__header`}
						id="principals"
					>
						Removing Principal
					</div>
					<div className="removing-principal-edit--button">
						<Button
							data-testid="edit-button"
							text="Edit"
							ghost
							submit={() => prevStep()}
						/>
					</div>
				</div>
				{principals?.length > 0 ? (
					<>
						{principals
							.map((item) => {
								return (
									<div
										className="removing-principal_table__body__rows"
										key={item.id}
									>
										<div className="removing-principal_table_data__row">
											<div
												className="layout-request__tile__content__avatar"
												style={{
													backgroundImage: `url(${item?.organisation?.iconUrl})`,
												}}
											>
												{!item?.organisation?.iconUrl && <IconExxoMobil />}
											</div>
											<div className="removing-principal_data__col__org_name_uen">
												<div>{item.name}</div>
												<div>
													UEN:
													{item.uen}
												</div>
											</div>
										</div>
									</div>
								);
							})}
					</>
				) : (
					<div className="data-table__empty">
						<NoMessage text="No principal to remove" />
					</div>
				)}
			</div>
		</div>
	);
};

const EditPrincipalPage = ({ defaultModal = false }) => {

	/*=================================================1
		General
	/================================================*/

	const { goTo } = useNavigate();
	const { currentState } = useLocation();
	const [principals, setPrincipals] = useState([]);
	const { useCaseName, dataElementName, pitstopName, organisationName, isRemoveOnly = false } = currentState;
	const {
		useCaseId, dataElementId,
		enrolmentType, systemId, // Edit Principal for pitstop
		subscriptionType, participantId, // Edit Principal for subscription (participant)
	} = useParams();
	const selectedKey = enrolmentType ? 'enrolmentId' : 'subscriptionId';
	const removePrincipalType = (subscriptionType === 'provider') ? 'contributorView' : 'agentView'
	const type = enrolmentType ?? subscriptionType;
	const {
		visible: visibleConfirmation,
		show: showConfirmation,
		hide: hideConfirmation,
	} = useModal();
	const isAgentPrincipalPhase2Enabled = feature(featureConstant.AGENT_PRINCIPAL_PHASE_2);

	const showSuccessPage = (editType) => {
		goTo(`/use-cases/${useCaseId}/principal/success`, {
			useCaseName: useCaseName,
			dataElementName: dataElementName,
			dataElementId,
			enrolmentType: enrolmentType ?? subscriptionType,
			pitstopName: pitstopName,
			editType: editType,
			isEnrolment: !!enrolmentType || !subscriptionType,
		});
	};

	const { breadcrumbsItems } = usePage({
		title: `Edit Principal - ${useCaseName} - ${dataElementName}`,
		breadcrumbs: [
			"Dashboard",
			useCaseName,
			dataElementName,
			"Edit Principal",
		]
	});

	/*=================================================
		API
	/================================================*/

	const addArr = principals
		?.filter((p) => {
			return !p?.[selectedKey] && p.$selected === true;
		})
		.map((item) => {
			return { principalId: item.id, ...item };
		});

	const removeArr = principals
		?.filter((p) => {
			return p?.[selectedKey] && p.$selected === false;
		})
		.map((item) => {
			return { principalId: item.id, ...item };
		});

	const submit = async () => {
		let payload = {
			add: (isRemoveOnly ? [] : addArr) || [],
			remove: removeArr || [],
		};

		if (isSubmitting) return;

		setIsSubmitting(true);

		let error, response;
		if (enrolmentType && systemId) {
			[error, response] = await API_ENROLMENT.put(
				currentState?.[selectedKey],
				payload
			);
		} else if (subscriptionType && participantId) {
			// Editing SubscriptionPrincipal as a COBOP or as a PROVIDER
			if (subscriptionType?.toUpperCase() === EnrolmentTypes.consumeOnBehalfOfPrincipal ||
				subscriptionType?.toUpperCase() === EnrolmentTypes.provider
			) {
				payload = { consumerPrincipals: payload }
			} else {
				payload = { providerPrincipals: payload }
			}
			[error, response] = await API_SUBSCRIPTIONS.updateParticipantSubscription(
				participantId,
				useCaseId,
				dataElementId,
				subscriptionType?.toUpperCase(),
				payload
			)
		}
		setIsSubmitting(false);
		if (!error) {
			if (addArr?.length > 0 && removeArr?.length > 0)
				showSuccessPage("Add&Remove");
			else if (addArr?.length > 0)
				showSuccessPage("Add");
			else if (removeArr?.length > 0)
				showSuccessPage("Remove");
		}
	};

	/*=================================================
		Principal
	/================================================*/

	const { getEditEnrollmentPrincipal, getSubscriptionPrincipal, items: editPrincipalData } = usePrincipals();

	function getSortedEnrolmentData(id, deId, type, sysId) {
		getEditEnrollmentPrincipal({
			useCaseId: id,
			dataElementId: deId,
			type: type?.toUpperCase(),
			systemId: sysId,
		});
	}

	function getSubscriptionPrincipalData(id, deId, type, participantId) {
		getSubscriptionPrincipal({
			useCaseId: id,
			dataElementId: deId,
			type: type?.toUpperCase(),
			participantId: participantId,
			edit: true,
		});
	}

	useEffect(() => {
		if (enrolmentType && systemId) {
			getSortedEnrolmentData(useCaseId, dataElementId, enrolmentType, systemId);
		} else if (subscriptionType && participantId) {
			getSubscriptionPrincipalData(useCaseId, dataElementId, subscriptionType, participantId)
		}
	}, []);

	useEffect(() => {
		let filterPrincipalArray;
		if (editPrincipalData?.length) {
			filterPrincipalArray = produce(editPrincipalData, draft => {
				draft.forEach((principal) => {
					principal.$selected = !!principal?.[selectedKey];
					principal.$disabled = isRemoveOnly ? !principal?.[selectedKey] : false;
				});

				draft.sort((a, b) => b.$selected - a.$selected);
			});
			if(isRemoveOnly){
				filterPrincipalArray= filterPrincipalArray?.filter((p)=>{
					return isRemoveOnly && !p.$disabled
				});
			}
			setPrincipals(filterPrincipalArray);

		}
	}, [editPrincipalData]);

	const [searchPrincipal, setSearchPrincipal] = useState("");
	const [modalOpen, isModalOpen] = useState(defaultModal);
	const [isSubmitting, setIsSubmitting] = useState(false);
	function onBack() {
		goTo(-1);
	}

	const pageHeaderTitle = "Edit Principal";
	const stepsArr=[
		{stepTitle: "Choose Principal"},
		{stepTitle: "Review & Confirm"}
	]
	const [steps] = useState([...stepsArr]);

	const { step, goToStep, prevStep, nextStep } = useStepList(steps);

	/*=================================================
		Confirmation List
	/================================================*/

	const ConfirmationList = () => {
		return (
			<>
				{!isRemoveOnly && (
					<AddingPrincipalTable principals={addArr} prevStep={prevStep} />
				)}
				<RemovingPrincipalTable principals={removeArr} prevStep={prevStep} />
			</>
		);
	};

	return (
		<>
			<div className="title-header">
				<div className="title-header__left">
					<div
						className="title-header__title"
						data-testid="title-header__title"
					>
						{pageHeaderTitle}
					</div>
				</div>
				<div className="title-header__right"></div>
			</div>

			<Stepper steps={steps} currentStep={step-1}/>

			<div style={{ marginTop: rem(10) }}></div>

			{step === 1 && (
				<>
					<div className="layout-enroll">
						<div className="layout-select-principal-header">
							<div className="view-info--details">
								<div className="view-info--selector-box-wrap">
									<div className="request-enrolment-toggle__cols">
										<div
											className={`layout-default__desc__principal ${isAgentPrincipalPhase2Enabled ? 'font-weight-bold' : ''}`}
											data-testid="layout-default__desc__principal"
										>
											Select Principal:
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					<div className="data-table__content">
						<div className="principal-table__table__head__row__left data-table__table__display">
							<Input
								maxWidth="334"
								fullWidth
								placeholder="Search by Principal name or UEN"
								prefix={<IconSearch />}
								value={searchPrincipal || ""}
								onChange={(e) => {
									setSearchPrincipal(e.target.value);
								}}
								autoFocus={modalOpen ? "" : "autoFocus"}
								cancelIcon={<IconClose />}
							/>
							<div>
								{principals?.filter(s => s?.$selected)?.length > 0 && (
									<span className={isAgentPrincipalPhase2Enabled ? 'font-weight-bold' : ''}>{principals?.filter(s => s?.$selected)?.length || 0} Selected</span>
								)}
							</div>
						</div>
						<PrincipalTableList
							principals={principals}
							setPrincipals={setPrincipals}
							searchPrincipal={searchPrincipal}
							titleHeader={"Principals"}
							pagination={true}
							orderBySelectedKey={true}
							selectedKey={selectedKey}
						/>
					</div>
				</>
			)}
			{step === 2 && <ConfirmationList />}
			<div className='DS__layout__footer'>
				<div className="layout-default__sticky-actions">
					<div className="layout-default__sticky-actions__left">
						<Button ghost text="Cancel" noborder submit={() => onBack()} />
					</div>
					<div className="layout-default__sticky-actions__right layout-default--flex">
						{step === 1 && (
							<>
								<Button
									data-testid="next-button"
									disabled={addArr?.length === 0 && removeArr?.length === 0}
									text="Next"
									submit={() => nextStep()}
								/>
							</>
						)}
						{step === 2 && (
							<>
								<Button
									addClass={"previous-button"}
									data-testid="previous-button"
									text="Previous"
									ghost
									submit={() => prevStep()}
								/>
								<Button
									data-testid="save-button"
									text="Submit"
									id="edit-personas-submit-button"
									loading={isSubmitting}
									submit={() => {
										if (removeArr?.length > 0) showConfirmation()
										else {
											submit();
										}
									}}
								/>
							</>
						)}
					</div>
				</div>
			</div>

			<ConfirmRemovalOfPrincipalModal
				visible={visibleConfirmation}
				hide={hideConfirmation}
				principals={principals}
				selectedKey={selectedKey}
				removePrincipalType={removePrincipalType}
				submit={submit}
				type={type}
				dataElementName={dataElementName}
				organisationName={organisationName}
			/>

		</>
	);
};

export default EditPrincipalPage;
